import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Timer, CheckCircle2, Users, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { ShareWin } from "../components/WinShareCard";
import { formatCoins } from "../lib/format";
import { playClick, playWin, playLose, playNewRound } from "../lib/sound";

const numColor = (n) => (n === 0 ? ["red", "violet"] : n === 5 ? ["green", "violet"] : n % 2 === 1 ? ["green"] : ["red"]);
const dotColor = (colors) =>
  colors.includes("violet") && colors.length > 1
    ? "linear-gradient(135deg,#00E676 50%,#9D4EDD 50%)"
    : colors.includes("green") ? "#00E676"
    : colors.includes("violet") ? "#9D4EDD" : "#FF3366";

export default function ColorPrediction() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(50);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [round, setRound] = useState(null);
  const [myBets, setMyBets] = useState([]);
  const [reveal, setReveal] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);
  const [feed, setFeed] = useState([]);

  const prevPeriod = useRef(null);
  const myBetsRef = useRef([]);
  useEffect(() => { myBetsRef.current = myBets; }, [myBets]);

  const loadFeed = useCallback(() => api.get("/games/color/feed").then(({ data }) => setFeed(data)).catch(() => {}), []);

  const onRoundEnd = useCallback(async (endedPeriod, recent) => {
    const mine = myBetsRef.current.filter((b) => b.period === endedPeriod);
    const resItem = (recent || []).find((r) => r.period === endedPeriod);
    try {
      const { data } = await api.get("/games/color/history");
      const rows = data.filter((r) => r.period === endedPeriod);
      const num = resItem ? resItem.number : (rows[0] ? rows[0].number : null);
      if (rows.length > 0) {
        const net = rows.reduce((s, r) => s + (r.net || 0), 0);
        const win = rows.reduce((s, r) => s + (r.win || 0), 0);
        setReveal({ number: num, net, win, won: win > 0, hadBet: true });
        if (win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(win); toast.success(`${t("youWon")} +${formatCoins(win)}!`); }
        else { playLose(); toast(`${t("youLost")} (${formatCoins(net)})`); }
      } else if (num != null) {
        setReveal({ number: num, net: 0, win: 0, won: null, hadBet: false });
      }
    } catch { /* ignore */ }
    setMyBets((prev) => prev.filter((b) => b.period !== endedPeriod));
  }, [t]);

  const loadRound = useCallback(async () => {
    try {
      const { data } = await api.get("/games/color/round");
      if (data.period !== prevPeriod.current) {
        if (prevPeriod.current != null) {
          onRoundEnd(prevPeriod.current, data.recent);
          playNewRound();
          toast(`🎲 ${t("newRound")}`, { duration: 2500 });
          loadFeed();
        }
      }
      prevPeriod.current = data.period;
      setRound(data);
    } catch { /* ignore */ }
  }, [onRoundEnd, loadFeed, t]);

  useEffect(() => { loadFeed(); loadRound(); const id = setInterval(loadRound, 1000); return () => clearInterval(id); }, [loadRound, loadFeed]);

  const betting = round?.phase === "betting";
  const bots = round?.bets || [];
  const confirm = async () => {
    if (pick == null || busy || !betting) return;
    setBusy(true);
    playClick();
    try {
      const { data } = await api.post("/games/color", { bet, pick: String(pick), period: round.period });
      setCoins(data.coins);
      setMyBets((prev) => [...prev, { period: data.period, pick: String(pick), bet }]);
      setReveal(null);
      toast.success(`${t("confirmBet")} ✓`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const colorBtn = (id, label, cls, payout) => (
    <button key={id} onClick={() => setPick(id)} disabled={!betting}
      data-testid={`color-${id}-btn`}
      className={`relative rounded-xl py-5 font-display font-extrabold text-lg text-white transition-transform active:scale-95 ${cls} ${pick === id ? "ring-4 ring-white/80 scale-[1.03]" : "opacity-90"} ${!betting ? "opacity-50" : ""}`}>
      {label}
      <span className="absolute top-1.5 right-2 text-[10px] font-mono-num opacity-90">{payout}</span>
    </button>
  );

  const myRoundBets = myBets.filter((b) => b.period === round?.period);

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("colorGame")}</h1>
          <p className="text-sm text-slate-400 mt-1">{t("green")} 2× · {t("violet")} 4.5× · {t("red")} 2× · {t("number")} 9×</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">{t("period")}</p>
          <p className="font-mono-num text-sm font-bold text-amber-300" data-testid="color-period">{round?.period ?? "—"}</p>
        </div>
      </div>

      {/* Timer bar */}
      <div className={`mw-card p-4 flex items-center justify-between ${betting ? "border-emerald-500/40" : "border-amber-500/40"}`} data-testid="color-timer">
        <div className="flex items-center gap-2">
          <Timer size={20} className={betting ? "text-emerald-400" : "text-amber-400"} />
          <span className={`font-semibold ${betting ? "text-emerald-300" : "text-amber-300"}`}>
            {betting ? t("bettingOpen") : t("bettingClosed")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{t("timeLeft")}</span>
          <span className="grid place-items-center h-10 min-w-10 px-2 rounded-xl font-mono-num text-xl font-extrabold"
            style={{ color: betting ? "#00E676" : "#FFB300", background: "rgba(0,0,0,0.4)" }} data-testid="color-countdown">
            {round?.seconds_left ?? 0}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_1fr] gap-5 items-start">
        <div className="mw-card p-4 space-y-4 order-last lg:order-first" data-testid="color-live-panel">
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-cyan-300 mb-2">
              <Users size={13} /> {t("liveBets")} {bots.length > 0 && <span className="text-slate-500">· {bots.length}</span>}
            </p>
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="color-live-bets">
              {bots.length === 0 && <p className="text-xs text-slate-600">—</p>}
              {bots.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg bg-black/30 px-2.5 py-1.5 text-xs">
                  <span className="font-mono-num font-semibold text-cyan-200 truncate" data-testid={`color-bet-uid-${b.id}`}>{b.user_id}</span>
                  <span className="grid place-items-center h-5 min-w-5 px-1 rounded text-[10px] font-bold text-white"
                    style={{ background: b.pick.match(/\d/) ? dotColor(numColor(Number(b.pick))) : (b.pick === "green" ? "#00E676" : b.pick === "red" ? "#FF3366" : "#9D4EDD") }}>
                    {b.pick}
                  </span>
                  <span className="text-slate-500">{formatCoins(b.bet)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-300 mb-2"><Trophy size={13} /> {t("topWinners")}</p>
            <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="color-winners">
              {feed.length === 0 && <p className="text-xs text-slate-600">—</p>}
              {feed.map((f, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-black/30 px-2.5 py-1.5 text-xs">
                  <span className="font-mono-num font-semibold text-slate-300 truncate">{f.user_id || f.name}</span>
                  <span className="font-mono-num text-emerald-400 font-bold">+{formatCoins(f.win)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mw-card p-6 space-y-5">
          <p className="text-xs font-semibold tracking-wider uppercase text-amber-400/90">{t("pickColor")}</p>
          <div className="grid grid-cols-3 gap-3">
            {colorBtn("green", t("green"), "bg-gradient-to-br from-emerald-400 to-emerald-600", "2×")}
            {colorBtn("violet", t("violet"), "bg-gradient-to-br from-violet-400 to-purple-700", "4.5×")}
            {colorBtn("red", t("red"), "bg-gradient-to-br from-rose-400 to-rose-600", "2×")}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: 10 }).map((_, n) => (
              <button key={n} onClick={() => setPick(n)} disabled={!betting} data-testid={`color-num-${n}`}
                className={`aspect-square rounded-lg font-mono-num font-bold text-lg border transition-transform active:scale-90 ${pick === n ? "btn-gold border-transparent scale-105" : "border-amber-500/30 bg-black/30 text-amber-200"} ${!betting ? "opacity-50" : ""}`}
                style={pick !== n ? { color: dotColor(numColor(n)) === "#FF3366" ? "#FF6b86" : dotColor(numColor(n)) } : {}}>
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="mw-card p-6 space-y-5">
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={!betting} />
          <button onClick={confirm} disabled={busy || pick == null || !betting} data-testid="color-play-btn"
            className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : betting ? <CheckCircle2 size={18} /> : <Timer size={18} />}
            {betting ? t("confirmBet") : t("waitingResult")}
          </button>

          {myRoundBets.length > 0 && (
            <div data-testid="color-mybets">
              <p className="text-xs font-semibold tracking-wider uppercase text-cyan-400/90 mb-2">{t("myBets")}</p>
              <div className="flex flex-wrap gap-1.5">
                {myRoundBets.map((b, i) => (
                  <span key={i} className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">
                    {b.pick} · {formatCoins(b.bet)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence>
            {reveal && (
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
                className="rounded-xl p-4 text-center border border-amber-500/30 bg-black/30" data-testid="color-result">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t("resultLabel")}</p>
                <div className="mx-auto mb-2 grid place-items-center h-16 w-16 rounded-full font-display text-2xl font-extrabold text-white"
                  style={{ background: dotColor(numColor(reveal.number ?? 0)) }}>
                  {reveal.number}
                </div>
                {reveal.hadBet && (
                  <>
                    <p className="font-display text-xl font-bold" style={{ color: reveal.won ? "#00E676" : "#FF3366" }}>
                      {reveal.won ? `+${formatCoins(reveal.win)}` : `${formatCoins(reveal.net)}`}
                    </p>
                    {reveal.won && <div className="mt-2 flex justify-center"><ShareWin amount={reveal.win} game="color" /></div>}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-amber-400/90 mb-2">{t("history")}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="color-history">
              {(round?.recent || []).map((h, i) => (
                <span key={i} className="grid place-items-center h-7 w-7 rounded-full text-xs font-bold text-white font-mono-num" style={{ background: dotColor(h.colors) }}>
                  {h.number}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
